import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { toast } from 'sonner';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const CumplimientoPlanEstudios = () => {
  const { user, API } = useContext(AuthContext);
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claseSeleccionada, setClaseSeleccionada] = useState('');
  const [clasesDisponibles, setClasesDisponibles] = useState([]);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const contenidoPdfRef = useRef(null);

  const COLORES = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899'];

  useEffect(() => {
    if (user) {
      cargarClasesDisponibles();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      cargarDatos();
    }
  }, [user, claseSeleccionada]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const params = {};
      if (claseSeleccionada) {
        params.clase_id = claseSeleccionada;
      }
      
      const response = await axios.get(`${API}/profesor/cumplimiento-plan-estudios`, { params });
      setDatos(response.data);
    } catch (error) {
      console.error('Error cargando cumplimiento:', error);
      toast.error('Error al cargar datos de cumplimiento');
    } finally {
      setLoading(false);
    }
  };

  const cargarClasesDisponibles = async () => {
    try {
      // Pedimos al mismo endpoint sin filtro para listar todas las clases activas
      const response = await axios.get(`${API}/profesor/cumplimiento-plan-estudios`);
      const lista = (response.data?.clases || []).map(c => ({
        id: c.clase_id,
        nombre: c.clase_nombre,
        profesor: c.profesor_nombre,
        semestres: c.semestres || [],
      }));
      // Ordenar por menor semestre y luego alfabético
      lista.sort((a, b) => {
        const sa = a.semestres.length ? Math.min(...a.semestres) : 999;
        const sb = b.semestres.length ? Math.min(...b.semestres) : 999;
        if (sa !== sb) return sa - sb;
        return a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' });
      });
      setClasesDisponibles(lista);
    } catch (error) {
      console.error('Error cargando clases:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Cargando Datos...</h3>
        </div>
      </div>
    );
  }

  if (!datos) {
    return <div className="p-6">No hay datos disponibles</div>;
  }

  // Preparar datos para gráficas (meta de 15 por clase, no programadas teóricas)
  const datosBarras = datos.clases.map(clase => ({
    nombre: clase.clase_nombre.length > 20 ? clase.clase_nombre.substring(0, 20) + '...' : clase.clase_nombre,
    meta: clase.meta_sesiones || 15,
    dictadas: clase.clases_dictadas,
    pendientes: Math.max(0, (clase.meta_sesiones || 15) - clase.clases_dictadas)
  }));

  const datosPie = [
    { name: 'Dictadas', value: datos.resumen.total_dictadas, color: '#10b981' },
    { name: 'Pendientes', value: datos.resumen.total_pendientes, color: '#ef4444' }
  ];

  const getColorPorcentaje = (porcentaje) => {
    if (porcentaje >= 80) return 'text-green-600 bg-green-50';
    if (porcentaje >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const generarPDF = async () => {
    const nodo = contenidoPdfRef.current;
    if (!nodo) {
      toast.error('No se encontró el contenido para exportar');
      return;
    }
    setGenerandoPdf(true);
    toast.info('Generando PDF... esto puede tardar unos segundos');
    try {
      // Esperar un tick para que recharts termine de renderizar si está animando
      await new Promise((r) => setTimeout(r, 400));

      const canvas = await html2canvas(nodo, {
        scale: 2,                 // alta calidad
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: nodo.scrollWidth,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 8;
      const printableWidth = pageWidth - margin * 2;
      const imgWidth = printableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Si el contenido entra en una sola página, lo colocamos tal cual.
      // Si no, lo partimos en páginas desplazando la Y de la imagen.
      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= (pageHeight - margin * 2);

      while (heightLeft > 0) {
        pdf.addPage();
        position = margin - (imgHeight - heightLeft); // desplaza hacia arriba
        pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight, undefined, 'FAST');
        heightLeft -= (pageHeight - margin * 2);
      }

      // Nombre del archivo
      const clase = clasesDisponibles.find((c) => c.id === claseSeleccionada);
      const slug = (clase ? clase.nombre : 'todas-las-clases')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const fecha = new Date().toISOString().slice(0, 10);
      pdf.save(`cumplimiento-${slug}-${fecha}.pdf`);
      toast.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('No se pudo generar el PDF. Intentá de nuevo.');
    } finally {
      setGenerandoPdf(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            <span className="mr-2">📊</span>
            Cumplimiento del Plan de Estudios
          </h1>
          <p className="text-gray-600 mt-1">
            Monitoreo de asistencia y cumplimiento de clases programadas
          </p>
        </div>

        {/* Selector de Clase (admin y profesor) */}
        <div className="w-80 flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">Filtrar por clase</label>
            <select
              value={claseSeleccionada}
              onChange={(e) => setClaseSeleccionada(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              data-testid="selector-clase"
            >
              <option value="">Todas las clases ({clasesDisponibles.length})</option>
              {clasesDisponibles.map(c => {
                const semStr = c.semestres.length ? `[S${[...c.semestres].sort((a,b)=>a-b).join(',S')}] ` : '';
                return (
                  <option key={c.id} value={c.id}>
                    {semStr}{c.nombre} — {c.profesor}
                  </option>
                );
              })}
            </select>
          </div>
          <Button
            onClick={generarPDF}
            disabled={generandoPdf}
            variant="outline"
            className="h-10 border-indigo-300 text-indigo-700 hover:bg-indigo-50 whitespace-nowrap"
            data-testid="btn-descargar-pdf"
            title="Descargar un PDF con la vista actual"
          >
            {generandoPdf ? (
              <><i className="fas fa-spinner fa-spin mr-1"></i>Generando...</>
            ) : (
              <><i className="fas fa-file-pdf mr-1 text-red-600"></i>Descargar PDF</>
            )}
          </Button>
        </div>
      </div>

      {/* Contenido que se exporta a PDF */}
      <div ref={contenidoPdfRef} className="space-y-6 bg-white p-2">
        {/* Encabezado dentro del PDF (visible siempre, también en pantalla) */}
        <div className="border-b-2 border-indigo-600 pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                📊 Cumplimiento del Plan de Estudios
              </h2>
              <p className="text-xs text-gray-600 mt-0.5">
                {claseSeleccionada
                  ? (() => {
                      const c = clasesDisponibles.find((x) => x.id === claseSeleccionada);
                      return c ? `Clase: ${c.nombre} — Profesor: ${c.profesor}` : 'Clase seleccionada';
                    })()
                  : `Todas las clases activas (${clasesDisponibles.length})`}
              </p>
            </div>
            <div className="text-right text-xs text-gray-500">
              <p>Instituto Bíblico Apostólico del Uruguay</p>
              <p>
                Generado el{' '}
                {new Date().toLocaleDateString('es-UY', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Resumen General - Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-2 border-blue-500">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">Total de Clases</p>
              <p className="text-4xl font-bold text-blue-600">
                {datos.resumen.total_clases}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-500">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">Meta Total (15 × clases)</p>
              <p className="text-4xl font-bold text-purple-600">
                {datos.resumen.total_programadas}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-500">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">Clases Dictadas</p>
              <p className="text-4xl font-bold text-green-600">
                {datos.resumen.total_dictadas}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-orange-500">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">% Cumplimiento</p>
              <p className={`text-4xl font-bold ${
                datos.resumen.porcentaje_general >= 80 ? 'text-green-600' :
                datos.resumen.porcentaje_general >= 60 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {datos.resumen.porcentaje_general}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfica de Barras - Comparativa por Clase */}
        <Card>
          <CardHeader>
            <CardTitle>📊 Meta vs Dictadas (sobre 15)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={datosBarras}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="meta" fill="#8b5cf6" name="Meta (15)" />
                <Bar dataKey="dictadas" fill="#10b981" name="Dictadas" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfica de Pie - Distribución General */}
        <Card>
          <CardHeader>
            <CardTitle>🥧 Distribución General</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={datosPie}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {datosPie.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Tabla Detallada por Clase */}
      <Card>
        <CardHeader>
          <CardTitle>📋 Detalle por Clase</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Clase
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Profesor
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Meta
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Dictadas
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Pendientes
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    % Cumplimiento
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {datos.clases.map((clase, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {clase.clase_nombre}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {clase.profesor_nombre}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-purple-600">
                      {clase.meta_sesiones || 15}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-green-600">
                      {clase.clases_dictadas}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-red-600">
                      {Math.max(0, (clase.meta_sesiones || 15) - clase.clases_dictadas)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getColorPorcentaje(clase.porcentaje_cumplimiento)}`}>
                        {clase.porcentaje_cumplimiento}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {clase.porcentaje_cumplimiento >= 80 ? (
                        <span className="text-2xl">✅</span>
                      ) : clase.porcentaje_cumplimiento >= 60 ? (
                        <span className="text-2xl">⚠️</span>
                      ) : (
                        <span className="text-2xl">❌</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Vista detallada por clase: grilla de 15 cuadrados, agrupada por semestre */}
      {datos.clases.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <i className="fas fa-th text-indigo-600"></i>
              Detalle por Clase (meta: 15 conexiones)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {(() => {
                // Paleta de colores por semestre (1-9)
                const SEMESTRE_COLORES = {
                  1: { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-800 border-rose-300', dot: 'bg-rose-500' },
                  2: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800 border-orange-300', dot: 'bg-orange-500' },
                  3: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500' },
                  4: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500' },
                  5: { bg: 'bg-teal-50', border: 'border-teal-300', text: 'text-teal-700', badge: 'bg-teal-100 text-teal-800 border-teal-300', dot: 'bg-teal-500' },
                  6: { bg: 'bg-sky-50', border: 'border-sky-300', text: 'text-sky-700', badge: 'bg-sky-100 text-sky-800 border-sky-300', dot: 'bg-sky-500' },
                  7: { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-800 border-indigo-300', dot: 'bg-indigo-500' },
                  8: { bg: 'bg-violet-50', border: 'border-violet-300', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-800 border-violet-300', dot: 'bg-violet-500' },
                  9: { bg: 'bg-pink-50', border: 'border-pink-300', text: 'text-pink-700', badge: 'bg-pink-100 text-pink-800 border-pink-300', dot: 'bg-pink-500' },
                };
                const colorPara = (n) => SEMESTRE_COLORES[n] || { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-800 border-gray-300', dot: 'bg-gray-500' };

                // Agrupar por menor semestre. Clases sin semestre => grupo "Sin semestre"
                const grupos = {};
                datos.clases.forEach((clase) => {
                  const sems = Array.isArray(clase.semestres) && clase.semestres.length > 0 ? clase.semestres : [];
                  const principal = sems.length > 0 ? Math.min(...sems) : 0;
                  if (!grupos[principal]) grupos[principal] = [];
                  grupos[principal].push(clase);
                });
                // Ordenar las claves numéricamente, "0" (sin semestre) al final
                const semOrdenados = Object.keys(grupos)
                  .map(Number)
                  .sort((a, b) => {
                    if (a === 0) return 1;
                    if (b === 0) return -1;
                    return a - b;
                  });
                // Ordenar alfabéticamente dentro de cada grupo
                semOrdenados.forEach((s) => {
                  grupos[s].sort((a, b) => a.clase_nombre.localeCompare(b.clase_nombre, 'es', { sensitivity: 'base' }));
                });

                return semOrdenados.map((sem) => {
                  const c = colorPara(sem);
                  const tituloSem = sem === 0 ? 'Sin semestre asignado' : `Semestre ${sem}`;
                  return (
                    <div key={`sem-${sem}`} className="space-y-3" data-testid={`grupo-semestre-${sem}`}>
                      {/* Header del semestre */}
                      <div className={`flex items-center gap-3 px-4 py-2 rounded-lg border ${c.border} ${c.bg}`}>
                        <span className={`w-3 h-3 rounded-full ${c.dot}`}></span>
                        <h3 className={`text-base font-bold ${c.text}`}>{tituloSem}</h3>
                        <span className={`text-xs ${c.text} opacity-75`}>
                          ({grupos[sem].length} {grupos[sem].length === 1 ? 'clase' : 'clases'})
                        </span>
                      </div>

                      {grupos[sem].map((clase, idx) => {
                        const meta = clase.meta_sesiones || 15;
                        const dictadas = clase.dictadas_detalle || [];
                        const cuadrados = Array.from({ length: meta }, (_, i) => dictadas[i] || null);
                        return (
                          <div
                            key={idx}
                            className={`border-l-4 ${c.border} bg-white rounded-lg p-4 shadow-sm`}
                            data-testid={`clase-grilla-${clase.clase_id}`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="text-base font-semibold text-gray-900">
                                    {clase.clase_nombre}
                                  </h4>
                                  {/* Mostrar todos los semestres a los que pertenece */}
                                  {Array.isArray(clase.semestres) && clase.semestres.length > 0 && (
                                    <div className="flex gap-1 flex-wrap">
                                      {[...clase.semestres].sort((a, b) => a - b).map((s) => {
                                        const cs = colorPara(s);
                                        return (
                                          <span
                                            key={s}
                                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cs.badge}`}
                                          >
                                            S{s}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {clase.profesor_nombre} · {dictadas.length}/{meta} clases dictadas
                                </p>
                              </div>
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                  dictadas.length >= meta * 0.8
                                    ? 'bg-green-100 text-green-700'
                                    : dictadas.length >= meta * 0.6
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {Math.round((dictadas.length / meta) * 100)}% sobre meta
                              </span>
                            </div>

                            {/* Grilla de 15 cuadrados */}
                            <div className="flex flex-wrap gap-2">
                              {cuadrados.map((d, i) => (
                                <div
                                  key={i}
                                  title={
                                    d
                                      ? `Clase ${i + 1} — ${d.fecha} a las ${d.hora}${
                                          d.ingreso_desde ? ' (' + d.ingreso_desde + ')' : ''
                                        }`
                                      : `Clase ${i + 1} — Pendiente`
                                  }
                                  className={`w-12 h-12 rounded-md flex items-center justify-center text-xs font-bold border transition-all hover:scale-105 cursor-default ${
                                    d
                                      ? 'bg-gradient-to-br from-indigo-500 to-blue-600 text-white border-indigo-700 shadow-sm'
                                      : 'bg-gray-100 text-gray-400 border-gray-200 border-dashed'
                                  }`}
                                  data-testid={`cuadrado-${clase.clase_id}-${i + 1}`}
                                >
                                  {i + 1}
                                </div>
                              ))}
                            </div>

                            {/* Lista de fechas dictadas (compacta) */}
                            {dictadas.length > 0 && (
                              <div className="mt-3 text-xs text-gray-500">
                                <span className="font-medium text-gray-600">Fechas dictadas: </span>
                                {dictadas.map((d, i) => (
                                  <span
                                    key={i}
                                    className="inline-block mr-2 mb-1 px-2 py-0.5 bg-indigo-50 rounded text-indigo-700"
                                  >
                                    {d.fecha} {d.hora}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                });
              })()}

              {/* Leyenda */}
              <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-sm bg-gradient-to-br from-indigo-500 to-blue-600 border border-indigo-700"></span>
                  Clase dictada (pasá el mouse para ver fecha y hora)
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-sm bg-gray-100 border border-gray-300 border-dashed"></span>
                  Pendiente
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
};

export default CumplimientoPlanEstudios;
